import { Component, OnInit, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl } from '@angular/forms';
import { HelperService } from 'src/app/services/utils/helper.service';
import { ResourceManagerService } from 'src/app/services/resourceManager/resource-manager.service';
import { GlobalService } from '../../global/global.service';
import { RequiredValidator } from 'src/app/customValidators/required/required';
import { Regex } from 'src/app/dataModels/regex';
import { AddressService } from 'src/app/services/address.service';
import { LOV_KEYS } from 'src/app/dataModels/lovs';
import { CONSTANTS, BUSY_MESSAGE } from 'src/app/dataModels/constants';
import { VendorService } from '../../interface/vendor.service';
import { EntityService } from '../../interface/entity.service';
import { EnrollmentData } from 'src/app/dataModels/enrollment-data';
import { FormDirective } from 'src/app/directives/form/form.directive';
import { CanComponentDeactivate } from 'src/app/can-deactivate.guard';
import { SessionService } from 'src/app/services/session/session.service';
import { SESSION_KEYS } from 'src/app/dataModels/session-keys';
import { SubmitEnrollmentData } from 'src/app/dataModels/submit-enrollment-data';
/**
 * enrollment form
 */
@Component({
  selector: 'app-enrollment',
  templateUrl: './enrollment.component.html',
  styleUrls: ['./enrollment.component.scss']
})
export class EnrollmentComponent implements OnInit, OnChanges, CanComponentDeactivate {
  /**
   * formgroup
   */
  enrollmentFrm: UntypedFormGroup;

  /**
   * mode of the form ADD or UPDATE
   */
  @Input() mode;

  /**
   * list of enroll in NJIIS
   */
  enrollNJIISList = [
    {
      descr: 'Yes',
      descrLong: '',
      displayOrder: '1',
      columnValue: 'Y',
      columnName: 'INTFC_FILE_TYPE',
      activeFlag: 'Y'
    },
    {
      descr: 'No',
      descrLong: '',
      displayOrder: '2',
      columnValue: 'N',
      columnName: 'INTFC_FILE_TYPE',
      activeFlag: 'N'
    }
  ];

  /**
   * list of states
   */
  stateList = [];

  /**
   * list of counties and municipalities
   */
  countyMuniList = [];

  /**
   * list of municipalities
   */
  municipalityList = [];

  /**
   * list of facilityType
   */
  facilityTypeList = [];

  /**
   * list of specialization
   */
  specializationList = [];

  /**
   * list of reason for Enrollment
   */
  reasonForEnrollmentList = [];

  /**
   * list of vaccines administered
   */
  vaccineAdministeredList = [];

  /**
   * list of patients under 7 years list
   */
  patientsUnder7YearsList = [];

  /**
   * list of intfcAccessType
   */
  intfcAccessTypeList = [];

  /**
   * list of intfcType
   */
  intfcTypeList = [];

  /**
   * list of message type
   */
  messageTypeList = [];

  /**
   * list of hl7 version
   */
  hl7VersionList = [];

  /**
   * list of vendors
   */
  vendorList;

  /**
   * list of intfcEntity list
   */
  intfcEntityList = [];

  /**
   * list of entity
   */
  entityList = [];

  /**
    * list of Enrollmment form entities
    */
  enrollEntityList = [];


  /**
   * list of entity Name
   */
  entityNameList = [];

  /**
   * boolean to indicate if form is submitted and to run validations
   */
  submit = false;

  /**
   * indicate if sftp section has to be displayed
   */
  showSFTPInfo = false;

  /**
   * indicate if component is busy making api calls
   */
  isBusy;

  /**
   * message to display during api calls
   */
  busyMessage;

  /**
   * entity intfc type list
   */
  entityIntfcTypeList = [];

  /**
   * enrollment data
   */
  @Input() enrollmentData: EnrollmentData;

  /**
   * reference to form directive
   */
  @ViewChild('focusInvalidElement') focusInvalidElement: FormDirective;

  /**
   * formData
   */
  formData = {
    isError: false,
    data: {} as SubmitEnrollmentData
  };

  /**
   * logged in user
   */
  user;

  /**
   * list of data submission options
   */
  dataSubmissionOptionsList = [];

  /**
   * all message type list
   */
  allMessageTypeList = [];

  /**
   * indicate if vendor is required
   */
  isVendorRequired = false;

  /**
   * constructor to inject dependency
   * @param helperService instance of helperService
   * @param resourceManagerService instance of resourceManagerService
   * @param globalService instance of globalService
   * @param addressService instance of addressService
   * @param vendorService instance of vendorService
   * @param entityService instance of entityService
   * @param sessionService instance of sessionService
   */
  constructor(
    private helperService: HelperService,
    private resourceManagerService: ResourceManagerService,
    private globalService: GlobalService,
    private addressService: AddressService,
    private vendorService: VendorService,
    private entityService: EntityService,
    private sessionService: SessionService
  ) { }

  /**
   * initialize component
   */
  ngOnInit(): void {
    this.initFormControls();
    this.getLovs();
    if (this.mode === CONSTANTS.UPDATE) {
      this.user = this.sessionService.getSessionData(SESSION_KEYS.USER);
      this.setCommentsRequired();
    } else if (this.mode === CONSTANTS.SUBMITTED ) {
      this.enrollmentFrm.disable();
    } else if (this.mode === CONSTANTS.ADD) {
      this.getVendorAndEntityList();
      this.setDefaults();
    }
  }

  /**
   * initialize form controls
   */
  initFormControls() {
    const ctrls = this.addressService.getAddressControls();
    this.enrollmentFrm = new UntypedFormGroup({
      practiceName: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.pattern(Regex.FACILITY_NAME),
        Validators.minLength(3),
        Validators.maxLength(100)
      ]),
      enrolledInNJIIS: new UntypedFormControl('', [Validators.required]),
      njiisFacilityId: new UntypedFormControl(''),
      vfcPin: new UntypedFormControl('', [Validators.pattern(Regex.GENERAL), Validators.maxLength(12)]),
      taxId: new UntypedFormControl('', [Validators.required]),
      primaryContactLastName: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(2),
        Validators.maxLength(30)
      ]),
      primaryContactFirstName: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(2),
        Validators.maxLength(30)
      ]),
      primaryContactMiddleName: new UntypedFormControl('', [
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(1),
        Validators.maxLength(30)
      ]),
      primaryTitle: new UntypedFormControl('', [Validators.pattern(Regex.GENERAL), Validators.minLength(2), Validators.maxLength(25)]),
      primaryPhone: new UntypedFormControl('', [Validators.required]),
      primaryPhoneExtn: new UntypedFormControl(''),
      primaryFax: new UntypedFormControl(''),
      primaryEmail: new UntypedFormControl('', [Validators.required, Validators.pattern(Regex.EMAIL)]),
      backupContactLastName: new UntypedFormControl('', [
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(2),
        Validators.maxLength(30)
      ]),
      backupContactFirstName: new UntypedFormControl('', [
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(2),
        Validators.maxLength(30)
      ]),
      backupContactMiddleName: new UntypedFormControl('', [
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(1),
        Validators.maxLength(30)
      ]),
      backupTitle: new UntypedFormControl('', [Validators.pattern(Regex.GENERAL), Validators.minLength(2), Validators.maxLength(25)]),
      backupPhone: new UntypedFormControl('', []),
      backupPhoneExtn: new UntypedFormControl(''),
      backupFax: new UntypedFormControl(''),
      backupEmail: new UntypedFormControl('', [Validators.pattern(Regex.EMAIL)]),
      practiceType: new UntypedFormControl('', [Validators.required]),
      specialization: new UntypedFormControl('', [Validators.required]),
      reasonForEnrollment: new UntypedFormControl('', [Validators.required]),
      yearlyDosesAdmin: new UntypedFormControl('', [Validators.required]),
      patientsUnder7Years: new UntypedFormControl('', [Validators.required]),
      vendorType: new UntypedFormControl(''),
      vendorName: new UntypedFormControl(''),
      vendorIdSeq: new UntypedFormControl(''),
      softwareName: new UntypedFormControl('', [Validators.minLength(3), Validators.maxLength(100), Validators.pattern(Regex.GENERAL)]),
      vendorPrimaryContactLastName: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(2),
        Validators.maxLength(30)
      ]),
      vendorPrimaryContactFirstName: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(2),
        Validators.maxLength(30)
      ]),
      vendorPrimaryContactMiddleName: new UntypedFormControl('', [
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(1),
        Validators.maxLength(30)
      ]),
      vendorPrimaryPhone: new UntypedFormControl('', [Validators.required]),
      vendorPrimaryPhoneExtn: new UntypedFormControl(''),
      vendorPrimaryFax: new UntypedFormControl(''),
      vendorPrimaryEmail: new UntypedFormControl('', [Validators.required, Validators.pattern(Regex.EMAIL)]),
      vendorBackupContactLastName: new UntypedFormControl('', [
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(2),
        Validators.maxLength(30)
      ]),
      vendorBackupContactFirstName: new UntypedFormControl('', [
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(2),
        Validators.maxLength(30)
      ]),
      vendorBackupContactMiddleName: new UntypedFormControl('', [
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(1),
        Validators.maxLength(30)
      ]),
      vendorBackupPhone: new UntypedFormControl('', []),
      vendorBackupPhoneExtn: new UntypedFormControl(''),
      vendorBackupFax: new UntypedFormControl(''),
      vendorBackupEmail: new UntypedFormControl('', [Validators.pattern(Regex.EMAIL)]),
      intfcAccessType: new UntypedFormControl('', [Validators.required]),
      intfcType: new UntypedFormControl('', [Validators.required]),
      messageType: new UntypedFormControl('', [Validators.required]),
      hl7Version: new UntypedFormControl('', [Validators.required]),
      entity: new UntypedFormControl(''),
      entityName: new UntypedFormControl(''),
      submittedBy: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern(Regex.FULL_NAME),
        Validators.minLength(2),
        Validators.maxLength(20)
      ]),
      sftpAccountName: new UntypedFormControl(''),
      oldComments: new UntypedFormControl({ value: '', disabled: true }),
      comments: new UntypedFormControl(''),
      dataSubmissionOption: new UntypedFormControl('')
    });
    for (const ctrl in ctrls) {
      if (ctrls.hasOwnProperty(ctrl)) {
        this.enrollmentFrm.addControl(ctrl, ctrls[ctrl]);
      }
    }
  }

  /**
   * getter for form controls
   */
  get f() {
    return this.enrollmentFrm?.controls;
  }

  /**
   * return vendorControls
   */
  get vendorControls() {
    return [
      {
        control: this.f.vendorIdSeq,
        validators: [Validators.required],
        nonDataSubmissionValidators: []
      },
      {
        control: this.f.vendorName,
        validators: [
         
        ],
        nonDataSubmissionValidators: [Validators.minLength(3), Validators.maxLength(100), Validators.pattern(Regex.FIRST_ALPHA_NUMERIC)]
      },
      {
        control: this.f.vendorPrimaryContactLastName,
        validators: [
          Validators.required,
          RequiredValidator.validateRequired,
          Validators.pattern(Regex.ANOTHER_NAME),
          Validators.minLength(2),
          Validators.maxLength(30)
        ],
        nonDataSubmissionValidators: [Validators.pattern(Regex.ANOTHER_NAME), Validators.minLength(2), Validators.maxLength(30)]
      },
      {
        control: this.f.vendorPrimaryContactFirstName,
        validators: [
          Validators.required,
          RequiredValidator.validateRequired,
          Validators.pattern(Regex.ANOTHER_NAME),
          Validators.minLength(2),
          Validators.maxLength(30)
        ],
        nonDataSubmissionValidators: [Validators.pattern(Regex.ANOTHER_NAME), Validators.minLength(2), Validators.maxLength(30)]
      },
      {
        control: this.f.vendorPrimaryPhone,
        validators: [Validators.required]
      },
      {
        control: this.f.vendorPrimaryEmail,
        validators: [Validators.required, Validators.pattern(Regex.EMAIL)],
        nonDataSubmissionValidators: [Validators.pattern(Regex.EMAIL)]
      }
    ];
  }

  /**
   * Check if control is invalid
   * @param ctrl ctrl to check invalidity for
   */
  isControlInvalid(ctrl: AbstractControl) {
    return this.helperService.isControlInvalid(this.submit, ctrl);
  }

  /**
   * get lovs
   * 1. LOV_KEYS.STATE
   * 2. HRCHY_KEYS.COUNTY_MUNI
   * 3. LOV_KEYS.FACILITY_TYPE
   * 4. LOV_KEYS.AF_SPECIALIZATION
   * 5. LOV_KEYS.INTFC_ENROLL_REASON
   * 6. LOV_KEYS.INTFC_ENROLL_DOSES_ADMIN
   * 7. LOV_KEYS.INTFC_ENROLL_PTS_UNDER_7
   * 8. LOV_KEYS.INTFC_ENROLL_ENTITY_SUB_TYPE
   * 9. LOV_KEYS.ENTITY_INTFC_TYPE
   * 10. LOV_KEYS.INTFC_ACCESS_TYPE
   * 11. LOV_KEYS.INTFC_FILE_TYPE
   * 12. LOV_KEYS.INTFC_FILE_VERSION
   * 13. LOV_KEYS.DATA_SUBMISSION_OPTIONS
   */
  async getLovs() {
    const request = [
      {
        key: LOV_KEYS.FACILITY_TYPE,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.AF_SPECIALIZATION,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.INTFC_ENROLL_REASON,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.INTFC_ENROLL_DOSES_ADMIN,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.INTFC_ENROLL_PTS_UNDER_7,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.ENTITY_SUB_TYPE,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.INTFC_ENROLL_ENTITY_SUB_TYPE,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.ENTITY_INTFC_TYPE,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.INTFC_ACCESS_TYPE,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.INTFC_ENROLL_ACCESS_TYPE,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.INTFC_FILE_TYPE,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.INTFC_FILE_VERSION,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.FACILITY_INTFC_TYPE,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.DATA_SUBMISSION_OPTIONS,
        type: CONSTANTS.LOV
      }
    ];
    this.addressService.getLovs().then(response => {
      this.stateList = response.stateList;
      this.countyMuniList = response.countyMuniList;
      this.stateChange();
    });

    if (this.mode === CONSTANTS.UPDATE) {
      await this.resourceManagerService.getResources(request).then(result => {
        this.assignLovs(result);
      });
    } else {
      await this.globalService
        .getResources(request)
        .then(result => {
          this.assignLovs(result);
        })
        .catch(e => console.log(e));
    }
  }

  /**
   * assign lovs to list
   * @param result lovs
   */
  assignLovs(result) {
    this.facilityTypeList = result[LOV_KEYS.FACILITY_TYPE];
    this.specializationList = result[LOV_KEYS.AF_SPECIALIZATION];
    this.reasonForEnrollmentList = result[LOV_KEYS.INTFC_ENROLL_REASON];
    this.vaccineAdministeredList = result[LOV_KEYS.INTFC_ENROLL_DOSES_ADMIN];
    this.patientsUnder7YearsList = result[LOV_KEYS.INTFC_ENROLL_PTS_UNDER_7];
    this.entityList = result[LOV_KEYS.ENTITY_SUB_TYPE];
    this.enrollEntityList = result[LOV_KEYS.INTFC_ENROLL_ENTITY_SUB_TYPE];
    this.entityIntfcTypeList = result[LOV_KEYS.ENTITY_INTFC_TYPE];
    this.intfcAccessTypeList = result[LOV_KEYS.INTFC_ENROLL_ACCESS_TYPE];
    this.allMessageTypeList = result[LOV_KEYS.INTFC_FILE_TYPE];
    this.messageTypeList = this.allMessageTypeList.filter(x => x.columnValue !== 'EXCEL');
    this.hl7VersionList = result[LOV_KEYS.INTFC_FILE_VERSION];
    this.intfcTypeList = result[LOV_KEYS.FACILITY_INTFC_TYPE];
    this.dataSubmissionOptionsList = result[LOV_KEYS.DATA_SUBMISSION_OPTIONS].filter(x => x.columnValue !== '1');
  }

  /**
   * get Vendor and Entity List
   */
  async getVendorAndEntityList() {
    await this.getVendorList();
    if (this.mode === CONSTANTS.UPDATE) {
      await this.getEntityList();
    }
  }
  /**
   * get list of vendors
   */
  async getVendorList() {
    await this.vendorService.getVendorList(true).then(result => {
      this.vendorList = result;
    });
  }

  /**
   * get list of entities
   */
  async getEntityList() {
    await this.entityService.getEntityList().then(result => {
      this.intfcEntityList = result;
    });
  }

  /**
   * on changing enrolled in NJIIS
   */
  enrolledInNJIISChange() {
    if (this.f.enrolledInNJIIS.value === 'Y') {
      this.f.njiisFacilityId.setValidators([Validators.required, Validators.pattern(Regex.NUMBER)]);
    } else {
      this.f.njiisFacilityId.clearValidators();
    }

    this.f.njiisFacilityId.updateValueAndValidity();
  }

  /**
   * On state change, enable, disable county, municipality controls
   * 1. If state is not NJ, clear County and Municipality and disable County and Municipality
   * 2. If State is NJ, clear County and Municipality and enable County and Municipality
   */
  stateChange() {
    const stateChangeObj = this.addressService.stateChange(this.enrollmentFrm);
    this.countyChange();
    this.addressService.modifyCountyMuniControls(this.enrollmentFrm, stateChangeObj.countyMuniValue, stateChangeObj.isDisabled);
  }

  /**
   * On county change, re-load Municipality List with selected county
   */
  countyChange() {
    const countyObj = this.addressService.countyChange(this.enrollmentFrm);
    this.municipalityList = countyObj.municipalityList;
  }

  /**
   * on changing vendor type
   */
  vendorTypeChange() {
    if (this.f.vendorType.value === 'E') {
      this.f.vendorName.clearValidators();
      this.f.vendorIdSeq.setValidators(Validators.required);
      this.f.vendorIdSeq.updateValueAndValidity();
      this.f.vendorName.updateValueAndValidity();
    } else {
      this.f.vendorIdSeq.clearValidators();
      this.f.vendorIdSeq.updateValueAndValidity();
      this.f.vendorName.setValidators([
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
        Validators.pattern(Regex.FIRST_ALPHA_NUMERIC)
      ]);
      this.f.vendorName.updateValueAndValidity();
    }
  }

  /**
   * on changing interface access type
   */
  intfcAccessTypeChange() {
    this.f.intfcType.setValue('');
    this.f.entity.setValue('');
    this.f.messageType.setValue('');
    this.f.hl7Version.setValue('');
    if (this.f.intfcAccessType.value !== '1') {
      this.f.intfcType.setValue('3');
      this.f.messageType.setValue('HL7');
      this.f.hl7Version.setValue('2');
      this.f.intfcType.disable();
    } else {
      this.f.intfcType.enable();
    }
  }

  /**
   * on changing interface type
   */
  intfcTypeChange() {
    this.f.entity.setValue('');
    this.f.messageType.setValue('');
    this.f.hl7Version.setValue('');
    this.entityChange();
    if (!this.helperService.IsNullOrEmpty(this.f.intfcAccessType.value) && !this.helperService.IsNullOrEmpty(this.f.intfcType.value)) {
      this.f.messageType.setValue('HL7');
      this.f.hl7Version.setValue('2');
    }
    if (this.f.intfcType.value === '2') {
      this.f.entity.disable();
    } else {
      this.f.entity.enable();
    }
    this.showSFTPInfo = this.f.intfcType.value === '1';
    this.f.sftpAccountName.setValue('');
  }

  /**
   * on entity selection
   */
  entityChange() {
    this.f.sftpAccountName.setValue('');
    if ((this.f.intfcType.value === '1' || this.f.intfcType.value === '3') && !this.helperService.IsNullOrEmpty(this.f.entity.value)) {
      this.entityNameList = [];
      const matchedEntityIntfcList = this.entityIntfcTypeList.filter(x => x.descr === 'SFTP and Web Service');

      for (const entity of this.intfcEntityList) {
        if (
          entity.intfcEntity.intfcEntitySubType === this.f.entity.value &&
          (entity.intfcEntity.intfcType === this.f.intfcType.value ||
            entity.intfcEntity.intfcType === matchedEntityIntfcList[0].columnValue) &&
          entity.intfcEntity.intfcEntityStatus === 'ACTIVE'
        ) {
          this.entityNameList.push(entity);
          this.f.entityName.setValidators(Validators.required);
        }
      }
    } else {
      this.entityNameList = [];
      this.f.entityName.clearValidators();
    }

    this.f.entityName.updateValueAndValidity();
  }

  /**
   * entity name change
   */
  entityNameChange() {
    if (this.f.intfcType.value === '1') {
      for (const entity of this.intfcEntityList) {
        if (entity.entityIdSeq === this.f.entityName.value) {
          this.f.sftpAccountName.setValue(entity.intfcProfile.sftpAccountId);
        }
      }
    } else {
      this.f.sftpAccountName.setValue('');
    }
  }

  /**
   * on changing reason for enrollment, clear the value of data submission option
   */
  reasonForEnrollmentChange() {
    this.f.dataSubmissionOption.setValue('');
    this.dataSubmissionOptionChange();
  }

  /**
   * on change data submission change, default interface profile
   */
  dataSubmissionOptionChange() {
    this.f.intfcType.setValue('');
    this.f.entity.setValue('');
    this.f.messageType.setValue('');
    this.f.hl7Version.setValue('');
    this.setDropdownValuesAndValidatorsOnDataSubmissionOptionChange();
    if (this.f.dataSubmissionOption.value === '3') {
      this.f.intfcAccessType.setValue('1');
      this.f.intfcType.setValue('2');
    } else {
      this.f.intfcAccessType.setValue('');
    }
  }

  /**
   * set default values on data submission option change
   */
  setDropdownValuesAndValidatorsOnDataSubmissionOptionChange() {
    if (this.f.dataSubmissionOption.value === '3') {
      this.messageTypeList = this.helperService.copyArrayWithObjects(this.allMessageTypeList);
      this.f.messageType.setValue('EXCEL');
      this.enableDisableInterfaceControls([this.f.intfcAccessType, this.f.intfcType, this.f.messageType], true);
      this.isVendorRequired = false;
      this.clearVendorValidators();
      this.clearHL7VersionValidators();
    } else {
      this.messageTypeList = this.allMessageTypeList.filter(x => x.columnValue !== 'EXCEL');
      this.enableDisableInterfaceControls([this.f.intfcAccessType, this.f.intfcType, this.f.messageType], false);
      this.isVendorRequired = true;
      if (this.f.vendorType.value !== 'N') {
        this.setVendorValidators();

      }
      this.setHL7VersionValidators();
    }
  }

  /**
   * enable of disable interface controls
   * @param ctrlList list of controls to be enabled or disabled
   * @param disable indicate if controls will be enabled or disabled
   */
  enableDisableInterfaceControls(ctrlList, disable) {
    for (const ctrl of ctrlList) {
      disable ? ctrl.disable() : ctrl.enable();
    }
  }

  /**
   * clear vendor validators
   */
  clearVendorValidators() {
    for (const ctrl of this.vendorControls) {
      ctrl.control.setValidators(ctrl.nonDataSubmissionValidators);
      ctrl.control.updateValueAndValidity();
    }
  }

  /**
   * set vendor validators
   */
  setVendorValidators() {
    for (const ctrl of this.vendorControls) {
      ctrl.control.setValidators(ctrl.validators);
      ctrl.control.updateValueAndValidity();
    }
  }

  /**
   * clear hl7 version validators
   */
  clearHL7VersionValidators() {
    this.f.hl7Version.clearValidators();
    this.f.hl7Version.updateValueAndValidity();
  }

  /**
   * clear hl7 version validators
   */
  setHL7VersionValidators() {
    this.f.hl7Version.setValidators([Validators.required]);
    this.f.hl7Version.updateValueAndValidity();
  }

  /**
   * set comments as required
   */
  setCommentsRequired() {
    this.f.comments.setValidators([Validators.required]);
    this.f.comments.updateValueAndValidity();
  }

  /**
   * assign form values
   */
  setFormData() {
    this.setFacilityInformation();
    this.setPrimaryContactInformation();
    this.setBackupContactInformation();
    this.setEnrollmentInformation();
    this.setVendorInformation();
    this.setInterfaceInformation();
    this.setOtherInformation();
    setTimeout(() => {
      this.enrollmentFrm.markAsPristine();
      this.enrollmentFrm.markAsUntouched();
    });
  }

  /**
   * set facility information
   */
  setFacilityInformation() {
    this.f.practiceName.setValue(this.enrollmentData.facilityName);
    this.f.enrolledInNJIIS.setValue(this.enrollmentData.njiisProviderFlag);
    this.enrolledInNJIISChange();
    this.f.njiisFacilityId.setValue(this.enrollmentData.njiisProviderSeq);
    this.f.vfcPin.setValue(this.enrollmentData.vfcId);
    this.f.taxId.setValue(this.enrollmentData.facilityTaxId);
    this.f.addressLine1.setValue(this.enrollmentData.addressLine1);
    this.f.addressLine2.setValue(this.enrollmentData.addressLine2);
    this.f.city.setValue(this.enrollmentData.city);
    this.f.state.setValue(this.enrollmentData.state);
    this.stateChange();
    this.f.zip.setValue(this.enrollmentData.zip);
    this.f.countyCode.setValue(this.enrollmentData.county?.substring(0, 2) + '00');
    this.countyChange();
    this.f.municipalityCode.setValue(this.addressService.getMunicipality(this.municipalityList, this.enrollmentData.county));
  }

  /**
   * set primary contact information
   */
  setPrimaryContactInformation() {
    this.f.primaryContactLastName.setValue(this.enrollmentData.providerContactLastName);
    this.f.primaryContactFirstName.setValue(this.enrollmentData.providerContactFirstName);
    this.f.primaryContactMiddleName.setValue(this.enrollmentData.providerContactMiddleName);
    this.f.primaryTitle.setValue(this.enrollmentData.providerContactTitle);
    this.f.primaryPhone.setValue(this.enrollmentData.phoneNumber);
    this.f.primaryPhoneExtn.setValue(this.enrollmentData.phoneExtn);
    this.f.primaryFax.setValue(this.enrollmentData.fax);
    this.f.primaryEmail.setValue(this.enrollmentData.emailAddr);
  }

  /**
   * set backupcontact information
   */
  setBackupContactInformation() {
    this.f.backupContactLastName.setValue(this.enrollmentData.provBkpContactLastName);
    this.f.backupContactFirstName.setValue(this.enrollmentData.provBkpContactFirstName);
    this.f.backupContactMiddleName.setValue(this.enrollmentData.provBkpContactMiddleName);
    this.f.backupTitle.setValue(this.enrollmentData.provBkpContactTitle);
    this.f.backupPhone.setValue(this.enrollmentData.bkpPhoneNumber);
    this.f.backupPhoneExtn.setValue(this.enrollmentData.bkpPhoneExtn);
    this.f.backupFax.setValue(this.enrollmentData.bkpFax);
    this.f.backupEmail.setValue(this.enrollmentData.bkpEmailAddr);
  }

  /**
   * set enrollment information
   */
  setEnrollmentInformation() {
    this.f.practiceType.setValue(this.enrollmentData.providerPracticeType);
    if (!this.helperService.IsNullOrEmpty(this.enrollmentData.provSpecialization)) {
      const specializationArr = this.enrollmentData.provSpecialization.split(',');
      const specializationList = [];
      for (const selectedSpecialization of specializationArr) {
        const matchedSpecialization = this.specializationList.filter(x => x.columnValue === selectedSpecialization);
        specializationList.push(matchedSpecialization[0]);
      }
      this.f.specialization.setValue(specializationList);
    }
    this.f.reasonForEnrollment.setValue(this.enrollmentData.reasonForEnrollment);
    this.f.yearlyDosesAdmin.setValue(this.enrollmentData.yearlyNumOfDosesAdmin);
    this.f.patientsUnder7Years.setValue(this.enrollmentData.totalPatientsUnderYears);
    this.f.dataSubmissionOption.setValue(this.enrollmentData.dataSubmissionOption);
  }

  /**
   * set vendor information
   */
  setVendorInformation() {
    if (this.helperService.IsNullOrEmpty(this.enrollmentData.vendorIdSeq) || this.enrollmentData.vendorIdSeq === '0') {
      this.f.vendorType.setValue('N');
      this.f.vendorName.setValue(this.enrollmentData.vendorName);
    } else {
      this.f.vendorType.setValue('E');
      this.f.vendorIdSeq.setValue(this.enrollmentData.vendorIdSeq);
    }

    this.f.softwareName.setValue(this.enrollmentData.softwareName);

    this.setVendorPrimaryContactInformation();
    this.setVendorBackupContactInformation();
  }

  /**
   * set vendor primary contact information
   */
  setVendorPrimaryContactInformation() {
    this.f.vendorPrimaryContactLastName.setValue(this.enrollmentData.vendorContactLastName);
    this.f.vendorPrimaryContactFirstName.setValue(this.enrollmentData.vendorContactLastName);
    this.f.vendorPrimaryContactMiddleName.setValue(this.enrollmentData.vendorContactMiddleName);
    this.f.vendorPrimaryPhone.setValue(this.enrollmentData.vendorPhoneNumber);
    this.f.vendorPrimaryPhoneExtn.setValue(this.enrollmentData.vendorPhoneExtn);
    this.f.vendorPrimaryFax.setValue(this.enrollmentData.vendorFax);
    this.f.vendorPrimaryEmail.setValue(this.enrollmentData.vendorEmailAddr);
  }

  /**
   * set vendor backup contact information
   */
  setVendorBackupContactInformation() {
    this.f.vendorBackupContactLastName.setValue(this.enrollmentData.vendBkpContactLastName);
    this.f.vendorBackupContactFirstName.setValue(this.enrollmentData.vendBkpContactFirstName);
    this.f.vendorBackupContactMiddleName.setValue(this.enrollmentData.vendBkpContactMiddleName);
    this.f.vendorBackupPhone.setValue(this.enrollmentData.vendBkpPhoneNumber);
    this.f.vendorBackupPhoneExtn.setValue(this.enrollmentData.vendBkpPhoneExtn);
    this.f.vendorBackupFax.setValue(this.enrollmentData.vendBkpFax);
    this.f.vendorBackupEmail.setValue(this.enrollmentData.vendBkpEmailAddr);
  }

  /**
   * set interface information
   */
  setInterfaceInformation() {
    this.f.intfcAccessType.setValue(this.enrollmentData.intfcAccessType);
    this.intfcAccessTypeChange();
    this.f.intfcType.setValue(this.enrollmentData.intfcType);
    this.intfcTypeChange();
    this.f.messageType.setValue(this.enrollmentData.fileFormat);
    this.f.hl7Version.setValue(this.enrollmentData.fileVersion);
    this.f.entity.setValue(this.enrollmentData.intfcEntityType);
    this.entityChange();
    this.f.entityName.setValue(this.enrollmentData.intfcEntityIdSeq);
    this.enrollmentFrm.markAsPristine();
    this.enrollmentFrm.markAsUntouched();
    if (this.showSFTPInfo) {
      this.f.sftpAccountName.setValue(this.enrollmentData.recomendedOutputDir);
    }
    this.setDropdownValuesAndValidatorsOnDataSubmissionOptionChange();
  }

  /**
   * set other information
   */
  setOtherInformation() {
    this.f.submittedBy.setValue(this.enrollmentData.submittedBy);
    this.f.oldComments.setValue(this.helperService.decodeJsonString(this.enrollmentData.comments));
  }

  /**
   * get form data
   */
  getFormData() {
    this.submit = true;
    if (this.enrollmentFrm.valid) {
      this.formData.isError = false;
      this.formData.data = this.getEnrollmentFormData();
    } else {
      this.formData.isError = true;
      setTimeout(() => {
        this.focusInvalidElement.onFormSubmit();
      });
    }

    return this.formData;
  }

  /**
   * get form data for saving
   */
  getEnrollmentFormData() {
    const enrollmentData: SubmitEnrollmentData = {} as SubmitEnrollmentData;

    this.getFacilityInformation(enrollmentData);
    this.getPrimaryContactInformation(enrollmentData);
    this.getBackupContactInformation(enrollmentData);
    this.getEnrollmentInformation(enrollmentData);
    this.getVendorInformation(enrollmentData);
    this.getInterfaceInformation(enrollmentData);
    this.getOtherInformation(enrollmentData);

    return enrollmentData;
  }

  /**
   * get facility information
   * @param enrollmentData enrollment data to be set
   */
  getFacilityInformation(enrollmentData: SubmitEnrollmentData) {
    enrollmentData.facilityName = this.f.practiceName.value;
    enrollmentData.njiisFacilityFlag = this.f.enrolledInNJIIS.value;
    enrollmentData.facilityId = this.f.njiisFacilityId.value;
    enrollmentData.vfcPin = this.f.vfcPin.value;
    enrollmentData.facilityTaxId = this.f.taxId.value;
    enrollmentData.addressLine1 = this.f.addressLine1.value;
    enrollmentData.addressLine2 = this.f.addressLine2.value;
    enrollmentData.city = this.f.city.value;
    enrollmentData.state = this.f.state.value;
    enrollmentData.zip = this.f.zip.value;
    enrollmentData.county = this.f.municipalityCode.value;
  }

  /**
   * get facility information
   * @param enrollmentData enrollment data to be set
   */
  getPrimaryContactInformation(enrollmentData: SubmitEnrollmentData) {
    enrollmentData.facilityContactLastName = this.f.primaryContactLastName.value;
    enrollmentData.facilityContactFirstName = this.f.primaryContactFirstName.value;
    enrollmentData.facilityContactMiddleName = this.f.primaryContactMiddleName.value;
    enrollmentData.facilityContactTitle = this.f.primaryTitle.value;
    enrollmentData.facilityContactPhoneNumber = this.f.primaryPhone.value;
    enrollmentData.facilityContactPhoneExtn = this.f.primaryPhoneExtn.value;
    enrollmentData.facilityContactFax = this.f.primaryFax.value;
    enrollmentData.facilityContactEmailAddr = this.f.primaryEmail.value;
  }

  /**
   * set backupcontact information
   * @param enrollmentData enrollment data to be set
   */
  getBackupContactInformation(enrollmentData: SubmitEnrollmentData) {
    enrollmentData.facilityBackupContactLastName = this.f.backupContactLastName.value;
    enrollmentData.facilityBackupContactFirstName = this.f.backupContactFirstName.value;
    enrollmentData.facilityBackupContactMiddleName = this.f.backupContactMiddleName.value;
    enrollmentData.facilityBackupContactTitle = this.f.backupTitle.value;
    enrollmentData.facilityBackupPhoneNumber = this.f.backupPhone.value;
    enrollmentData.facilityBackupPhoneExtn = this.f.backupPhoneExtn.value;
    enrollmentData.facilityBackupFax = this.f.backupFax.value;
    enrollmentData.facilityBackupEmailAddr = this.f.backupEmail.value;
  }

  /**
   * set enrollment information
   * @param enrollmentData enrollment data to be set
   */
  getEnrollmentInformation(enrollmentData: SubmitEnrollmentData) {
    enrollmentData.facilityType = this.f.practiceType.value;
    if (!this.helperService.IsNullOrEmpty(this.f.specialization.value)) {
      enrollmentData.facilitySpecialization = this.f.specialization.value.map(x => x.columnValue).join(',');
    }
    enrollmentData.reasonForEnrollment = this.f.reasonForEnrollment.value;
    enrollmentData.yearlyNumOfDosesAdmin = this.f.yearlyDosesAdmin.value;
    enrollmentData.totalPatientsUnder7Years = this.f.patientsUnder7Years.value;
    enrollmentData.dataSubmissionOption = this.f.dataSubmissionOption.value;
  }

  /**
   * set vendor information
   * @param enrollmentData enrollment data to be set
   */
  getVendorInformation(enrollmentData: SubmitEnrollmentData) {
    if (this.f.vendorType.value === 'N') {
      enrollmentData.vendorName = this.f.vendorName.value;
    } else {
      enrollmentData.vendorIdSeq = this.f.vendorIdSeq.value;
      enrollmentData.vendorName = this.getVendorName();
    }

    enrollmentData.softwareName = this.f.softwareName.value;

    this.getVendorPrimaryContactInformation(enrollmentData);
    this.getVendorBackupContactInformation(enrollmentData);
  }

  /**
   * get vendor name
   */
  getVendorName() {
    const matchedVendorNameList = this.vendorList.filter(x => x.vendorIdSeq === this.f.vendorIdSeq.value);
    return matchedVendorNameList?.length > 0 ? matchedVendorNameList[0].vendorName : '';
  }

  /**
   * set vendor primary contact information
   * @param enrollmentData enrollment data to be set
   */
  getVendorPrimaryContactInformation(enrollmentData: SubmitEnrollmentData) {
    enrollmentData.vendorContactLastName = this.f.vendorPrimaryContactLastName.value;
    enrollmentData.vendorContactFirstName = this.f.vendorPrimaryContactFirstName.value;
    enrollmentData.vendorContactMiddleName = this.f.vendorPrimaryContactMiddleName.value;
    enrollmentData.vendorContactPhoneNumber = this.f.vendorPrimaryPhone.value;
    enrollmentData.vendorContactPhoneExtn = this.f.vendorPrimaryPhoneExtn.value;
    enrollmentData.vendorContactFax = this.f.vendorPrimaryFax.value;
    enrollmentData.vendorContactEmailAddr = this.f.vendorPrimaryEmail.value;
  }

  /**
   * set vendor backup contact information
   * @param enrollmentData enrollment data to be set
   */
  getVendorBackupContactInformation(enrollmentData: SubmitEnrollmentData) {
    enrollmentData.vendorBackupContactLastName = this.f.vendorBackupContactLastName.value;
    enrollmentData.vendorBackupContactFirstName = this.f.vendorBackupContactFirstName.value;
    enrollmentData.vendorBackupContactMiddleName = this.f.vendorBackupContactMiddleName.value;
    enrollmentData.vendorBackupContactPhoneNumber = this.f.vendorBackupPhone.value;
    enrollmentData.vendorBackupContactPhoneExtn = this.f.vendorBackupPhoneExtn.value;
    enrollmentData.vendorBackupContactFax = this.f.vendorBackupFax.value;
    enrollmentData.vendorBackupContactEmailAddr = this.f.vendorBackupEmail.value;
  }

  /**
   * set interface information
   * @param enrollmentData enrollment data to be set
   */
  getInterfaceInformation(enrollmentData: SubmitEnrollmentData) {
    enrollmentData.intfcAccessType = this.f.intfcAccessType.value;
    enrollmentData.intfcType = this.f.intfcType.value;
    enrollmentData.fileFormat = this.f.messageType.value;
    enrollmentData.fileVersion = this.f.hl7Version.value;
    enrollmentData.intfcEntityType = this.f.entity.value;
    enrollmentData.intfcEntityIdSeq = this.f.entityName.value;
    if (this.showSFTPInfo) {
      enrollmentData.recomendedOutputDir = this.f.sftpAccountName.value;
    }
  }

  /**
   * set other information
   * @param enrollmentData enrollment data to be set
   */
  getOtherInformation(enrollmentData: SubmitEnrollmentData) {
    enrollmentData.submittedBy = this.f.submittedBy.value;
    if (this.mode === CONSTANTS.UPDATE) {
      enrollmentData.comments = this.helperService.appendUserAndDateForComments(this.f.comments.value, this.user?.userName, true);
    } else {
      enrollmentData.comments = this.helperService.encodeJsonString(this.f.comments.value);
    }
  }

  /**
   * set default during add mode
   */
  setDefaults() {
    this.f.vendorType.setValue('E');
    this.vendorTypeChange();
    this.enrollmentFrm.markAsPristine();
    this.enrollmentFrm.markAsUntouched();
  }

  /**
   * reset form data
   */
  reset() {
    this.submit = false;
    if (this.mode === CONSTANTS.ADD) {
      this.helperService.resetForm(this.enrollmentFrm);
      this.setDefaults();
    } else {
      this.setFormData();
      this.f.comments.setValue('');
    }
  }

  /**
   * on change of enrollment data set form data
   */
  ngOnChanges(changes: SimpleChanges) {
    if (this.mode === CONSTANTS.SUBMITTED ) {
      this.enrollmentFrm.disable();
    }
    if (changes?.enrollmentData && changes.enrollmentData.currentValue && !changes.enrollmentData.firstChange) {
      this.enrollmentData = changes.enrollmentData.currentValue;
      this.getVendorAndEntityList().then(() => {
        this.setFormData();
      });
    }
  }

  /**
   * warn user on unsaved changes
   */
  canDeactivate() {
    return this.helperService.canDeactivate(this.enrollmentFrm);
  }
}
